import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { IntakeQuestion } from "../models/IntakeQuestion";
import { IntakeQuestionService } from "../services/IntakeQuestionService";
import { useAuth } from "./AuthContext";
import { Event } from "../models/Event";
import {
  collection,
  doc,
  onSnapshot,
  orderBy,
  query,
} from "firebase/firestore";
import { db } from "../firebase";
import { useMatter } from "./MatterContext";
import { getSubdomainFromUrl } from "../utils/url";

interface Errors {
  list?: Error | null;
  details?: Error | null;
}
interface Loadings {
  list?: boolean;
  details?: boolean;
}

interface IntakeQuestionContextProps {
  intakeQuestions: IntakeQuestion[];
  intakeQuestionCounts: Record<IntakeQuestion["matterType"], number>;
  selectedIntakeQuestion: IntakeQuestion | null;
  setSelectedIntakeQuestion: (intakeQuestionId: string) => void;
  createIntakeQuestion: (
    intakeQuestionData: Omit<
      IntakeQuestion,
      "_id" | "createdAt" | "updatedAt" | "createdBy" | "matter"
    >,
  ) => Promise<string>;
  updateIntakeQuestion: (
    intakeQuestionId: string,
    updatedData: Partial<
      Omit<IntakeQuestion, "_id" | "createdAt" | "updatedAt">
    >,
  ) => void;
  deleteIntakeQuestionById: (intakeQuestionId: string) => void;
  loading: Loadings;
  error: Errors;
}

const IntakeQuestionContext = createContext<
  IntakeQuestionContextProps | undefined
>(undefined);

export const useIntakeQuestion = (): IntakeQuestionContextProps => {
  const context = useContext(IntakeQuestionContext);
  if (!context) {
    throw new Error(
      "useIntakeQuestion must be used within a IntakeQuestionProvider",
    );
  }
  return context;
};

export const IntakeQuestionProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { user } = useAuth();
  const [intakeQuestions, setIntakeQuestions] = useState<IntakeQuestion[]>([]);
  const [intakeQuestionCounts, setIntakeQuestionCounts] = useState<
    Record<IntakeQuestion["matterType"], number>
  >({});
  const [selectedIntakeQuestion, setSelectedIntakeQuestion] =
    useState<IntakeQuestion | null>(null);
  const [errors, setErrors] = useState<Errors>({});
  const [loadings, setLoadings] = useState<Loadings>({
    list: true,
    details: true,
  });

  useEffect(() => {
    if (!user) return;
    setLoadings((prev) => ({ ...prev, list: true }));
    const tenantId = getSubdomainFromUrl(window.location.href);
    const docRef = query(
      collection(db, "tenants", tenantId, "intakeQuestions"),
    );
    const unsubscribe = onSnapshot(
      docRef,
      (docSnapshot) => {
        setLoadings((prev) => ({ ...prev, list: false }));
        if (docSnapshot.empty) {
          setIntakeQuestions([]);
          return;
        }
        setIntakeQuestions(
          docSnapshot.docs.map(
            (doc) => ({ _id: doc.id, ...doc.data() }) as IntakeQuestion,
          ),
        );
      },
      (err) => {
        console.error("Failed to listen to intakeQuestion changes:", err);
        setErrors((prev) => ({
          ...prev,
          list: new Error(
            `Failed to listen to intakeQuestion changes: ${err.message}`,
          ),
        }));
        setLoadings((prev) => ({ ...prev, list: false }));
      },
    );
    return () => {
      unsubscribe();
    };
  }, [user]);

  useEffect(() => {
    if (!user || !selectedIntakeQuestion) return;
    setLoadings((prev) => ({ ...prev, details: true }));
    const tenantId = getSubdomainFromUrl(window.location.href);
    const docRef = doc(
      collection(db, "tenants", tenantId, "intakeQuestions"),
      selectedIntakeQuestion._id,
    );
    const unsubscribe = onSnapshot(
      docRef,
      (docSnapshot) => {
        if (!docSnapshot.exists()) {
          setSelectedIntakeQuestion(null);
          setErrors((prev) => ({
            ...prev,
            details: new Error("IntakeQuestion not found"),
          }));
        } else {
          const intakeQuestionData = {
            _id: docSnapshot.id,
            ...docSnapshot.data(),
          } as IntakeQuestion;
          setSelectedIntakeQuestion(intakeQuestionData);
          setErrors((prev) => ({ ...prev, details: null }));
        }
        setLoadings((prev) => ({ ...prev, details: false }));
      },
      (err) => {
        console.error("Failed to listen to intakeQuestion changes:", err);
        setErrors((prev) => ({
          ...prev,
          details: new Error(
            `Failed to listen to intakeQuestion changes: ${err.message}`,
          ),
        }));
        setLoadings((prev) => ({ ...prev, details: false }));
      },
    );
    return () => {
      unsubscribe();
    };
  }, [selectedIntakeQuestion?._id]);

  const setSelectedIntakeQuestionById = async (intakeQuestionId: string) => {
    const intakeQuestionData =
      await IntakeQuestionService.getIntakeQuestion(intakeQuestionId);
    setSelectedIntakeQuestion(intakeQuestionData);
  };

  const createIntakeQuestion = async (
    intakeQuestionData: Omit<
      IntakeQuestion,
      "_id" | "createdAt" | "updatedAt" | "createdBy" | "matter"
    >,
  ): Promise<string> => {
    const newIntakeQuestionId =
      await IntakeQuestionService.createIntakeQuestion(intakeQuestionData);
    return newIntakeQuestionId;
  };

  const updateIntakeQuestion = async (
    intakeQuestionId: string,
    updatedData: Partial<
      Omit<IntakeQuestion, "_id" | "createdAt" | "updatedAt">
    >,
  ) => {
    await IntakeQuestionService.updateIntakeQuestion(
      intakeQuestionId,
      updatedData,
    );
    setSelectedIntakeQuestionById(intakeQuestionId);
  };

  const deleteIntakeQuestionById = async (intakeQuestionId: string) => {
    await IntakeQuestionService.deleteIntakeQuestion(intakeQuestionId);
  };

  return (
    <IntakeQuestionContext.Provider
      value={{
        intakeQuestionCounts,
        loading: loadings,
        error: errors,
        intakeQuestions,
        selectedIntakeQuestion,
        createIntakeQuestion,
        updateIntakeQuestion,
        deleteIntakeQuestionById,
        setSelectedIntakeQuestion: setSelectedIntakeQuestionById,
      }}
    >
      {children}
    </IntakeQuestionContext.Provider>
  );
};
